import InterfaceHelper from '@unifyapps/carbon/no-code/stores/InterfaceStore/helper/InterfaceHelper';
import type { PageTransitionBehavior } from '@unifyapps/defs/types/interface';
import { useTranslation } from '@unifyapps/i18n/client';
import { useGetQueryMeta } from '@unifyapps/network/hooks/useGetQueryMeta';
import { useSnackbar } from '@unifyapps/ui/components/Snackbar';
import { isNative } from '@unifyapps/ui/utils/isNative';
import { useCallback } from 'react';
import useLink from '../../../../../hooks/useLink';
import { processUrl } from '../../../../../navigation/utils/processUrl';
import { handleBackNavigation } from '../../../../../navigation/ViewTransitions/PageTransitions';
import { NO_CODE_TEMPLATES_BY_ID } from '../../../../const/noCodeTemplates';
import type { ActionHandlerType, OnActionArgs } from '../../../../context/ActionsProvider/context';
import { skipURLStateUpdates } from '../../../../context/URLStateProvider/skipURLStateUpdates';
import { useGlobalAppStateStore } from '../../../../stores/app/GlobalAppStateStore';
import { useGlobalStateStore } from '../../../../stores/GlobalStateStore';
import {
  InterfaceModes,
  useGetInterfaceStoreState,
  useInterfaceStore,
} from '../../../../stores/InterfaceStore';
import checkBuilderMode from '../../../../utils/checkBuilderMode';
import {
  getInterfacePageBuilderRoute,
  getInterfacePageNavigationBuilderURL,
  getInterfacePageNavigationPreviewURL,
  getInterfacePreviewRoute,
} from '../../../../utils/interfaceRoutes';
import { parseSearchParamsFromSchema } from '../../../../utils/parseSearchParamsFromSchema';
import { resolveDynamicPath } from '../../../../utils/path/interfacePathDynamicParamsHandlers';
import { getNormalizedPathFromSlugOrPathNames } from '../../../../utils/path/interfacePathNormalize';
import { serializePageInputsToSearchParams } from '../../../../utils/serializePageInputsToSearchParams';
import { useGetNoCodeComputedData } from '../../../computeContext/useGetNoCodeComputedData';
import type { NavigationOptions } from '../../../useRouter';
import { useRouter } from '../../../useRouter';
import { SHOULD_NOT_PROPAGATE_UP } from '../../const';

export type NavigateToPagePayload = {
  pageId?: string;
  pageInputs?: Record<string, unknown>;
  pageParams?: { key?: string; value?: unknown }[];
  history?: 'push' | 'replace';
  navigateToSpecificPage?: boolean;
  overridePageAnimation?: boolean;
  pageTransitionBehavior?: PageTransitionBehavior;
};

export type SetURLParametersPayload = {
  pageInputs?: Record<string, unknown>;
  mergeWithExistingQueryParams?: boolean;
  history?: 'push' | 'replace';
};

type OnNavigatePayloadType = {
  path: string;
  pageInputs?: Record<string, unknown>;
  history?: 'push' | 'replace' | 'back';
  target?: unknown;
  /** mobile only: overrides target page transition behavior when not Default */
  pageTransitionBehavior?: PageTransitionBehavior;
};

const getUrl = ({
  interfacePagePath,
  interfaceRecordId,
  isPublic,
  mode,
  pathname,
}: {
  interfacePagePath: string;
  interfaceRecordId: string;
  mode: InterfaceModes;
  isPublic?: boolean;
  pathname: string;
}) => {
  const normalizedInterfacePagePath = getNormalizedPathFromSlugOrPathNames(interfacePagePath);

  switch (mode) {
    case InterfaceModes.RUNNER:
      if (isPublic) {
        return `/public/${normalizedInterfacePagePath}`;
      }
      return `/${normalizedInterfacePagePath}`;
    case InterfaceModes.BUILDER:
      return getInterfacePageBuilderRoute({
        interfaceId: interfaceRecordId,
        interfacePagePathOrSlug: normalizedInterfacePagePath,
      });

    case InterfaceModes.PREVIEW: {
      //  we are checking if we are in builder mode from url so that we can redirect to page in builder mode.
      // since we render pages and modules in drawer in preview mode; so, redirecting from there to some page
      //renders the page naturally in preview mode while we may actually be in builder mode
      if (checkBuilderMode(pathname)) {
        return getInterfacePageBuilderRoute({
          interfaceId: interfaceRecordId,
          interfacePagePathOrSlug: normalizedInterfacePagePath,
        });
      }

      return getInterfacePreviewRoute({
        interfaceId: interfaceRecordId,
        interfacePagePathOrSlug: normalizedInterfacePagePath,
      });
    }
    default:
      return null;
  }
};

const getLegacyUrl = ({
  interfacePageId,
  interfaceRecordId,
  mode,
  isPublic,
  pathname,
}: {
  interfacePageId: string;
  interfaceRecordId: string;
  mode: InterfaceModes;
  isPublic?: boolean;
  pathname: string;
}) => {
  switch (mode) {
    case InterfaceModes.RUNNER:
      if (isPublic) {
        return `/public/${interfacePageId}`;
      }
      return `/${interfacePageId}`;
    case InterfaceModes.BUILDER:
      return getInterfacePageNavigationBuilderURL(interfaceRecordId, interfacePageId);
    case InterfaceModes.PREVIEW: {
      //  we are checking if we are in builder mode from url so that we can redirect to page in builder mode.
      // since we render pages and modules in drawer in preview mode; so, redirecting from there to some page
      //renders the page naturally in preview mode while we may actually be in builder mode
      if (checkBuilderMode(pathname)) {
        return getInterfacePageNavigationBuilderURL(interfaceRecordId, interfacePageId);
      }
      return getInterfacePageNavigationPreviewURL(interfaceRecordId, interfacePageId);
    }
    default:
      return null;
  }
};
export default function useNavigateToPage() {
  const router = useRouter();
  const { pathname } = router;
  const mode = useGlobalAppStateStore().hooks.useMode();
  const basePath = useGlobalAppStateStore().hooks.useBasePath();
  const isPublic = useInterfaceStore().hooks.useIsPublic();
  const interfaceRecordId = useGlobalAppStateStore().hooks.record.useId();
  const pageTransition = useGlobalAppStateStore().hooks.record.usePageTransition();
  const { getInterfaceStoreState } = useGetInterfaceStoreState();
  const { showSnackbar } = useSnackbar();
  const { t } = useTranslation(['interfaces-matrix']);
  const { getNoCodeComputedData } = useGetNoCodeComputedData();
  const { openLink } = useLink();
  const queryMeta = useGetQueryMeta();

  const onNavigate = useCallback(
    (params: OnNavigatePayloadType) => {
      const { path, pageInputs, history, target, pageTransitionBehavior } = params;

      const queryParams = serializePageInputsToSearchParams(pageInputs);
      const queryString = queryParams.toString();

      const finalPath = basePath ? `${basePath}${path}` : path;
      const finalLink = queryString ? `${finalPath}?${queryString}` : finalPath;

      // pass pageTransitionBehavior via router options (consumed by React Native router)
      // only set when overridePageAnimation is ON
      const navOptions: NavigationOptions | undefined =
        pageTransitionBehavior && isNative() ? { pageTransitionBehavior } : undefined;

      skipURLStateUpdates();

      //if page navigation needs to happen in new tab
      switch (history) {
        case 'back':
          // when final link is given, we need to navigate to that page
          // for native it supports taking a finalLink in back parameter
          // in web back() doesn't take any link, we have to call router.push with the finalLink
          // if present in web, all this is done so that we can support moving to a page that is not in history stack of the browser but we want to show back view transition to that page
          if (pageTransition === 'SLIDE_HORIZONTAL') {
            handleBackNavigation();
          }
          if (finalLink) {
            if (isNative()) {
              router.back(finalLink, navOptions);
            } else {
              router.push(finalLink);
            }
          } else {
            router.back(undefined, navOptions);
          }
          break;
        case 'push':
          if (target === '_blank') {
            openLink(processUrl(finalLink, undefined, { client: undefined, queryMeta }));
          } else {
            router.push(finalLink, undefined, navOptions);
          }
          break;
        case 'replace':
          if (target === '_blank') {
            openLink(finalLink);
          } else {
            router.replace(finalLink, undefined, navOptions);
          }
          break;
        default:
          break;
      }
    },
    [basePath, openLink, router, queryMeta, pageTransition],
  );

  const getPathForInterfacePageId = useCallback(
    (interfacePageId: string, pageParamsKeyValues?: { key?: string; value?: unknown }[]) => {
      const interfaceRecord = getInterfaceStoreState().interfaceRecord;
      const interfacePageIdVsSlugMap = InterfaceHelper.getInterfacePageIdVsSlugMap(interfaceRecord);

      const standardPage = NO_CODE_TEMPLATES_BY_ID[interfacePageId];
      const interfacePageSlug =
        standardPage?.interfacePageSlug ?? interfacePageIdVsSlugMap?.[interfacePageId];

      const rawInterfacePagePath = InterfaceHelper.getFullRawNonNormalizedPathByPageId(
        interfacePageId,
        interfaceRecord,
      );

      // Resolve dynamic segments against real pageParams values BEFORE the path
      // reaches getUrl / getInterfacePageBuilderRoute, which would otherwise
      // replace :param segments with dummy values in builder mode.
      const interfacePagePathOrSlug = rawInterfacePagePath
        ? resolveDynamicPath(rawInterfacePagePath, pageParamsKeyValues)
        : interfacePageSlug;

      if (interfacePagePathOrSlug?.includes(':')) {
        console.error('Dynamic Params not passed properly', {
          interfacePageId,
          pageParamsKeyValues,
          interfacePagePathOrSlug,
        });
        if (mode !== InterfaceModes.RUNNER) {
          showSnackbar({
            color: 'error',
            title: t('interfaces-matrix:appActions.DynamicParamsNotPassedProperly'),
          });
        }
        return null;
      }

      return interfacePagePathOrSlug
        ? getUrl({
            interfacePagePath: interfacePagePathOrSlug,
            interfaceRecordId,
            mode,
            isPublic,
            pathname,
          })
        : getLegacyUrl({ interfacePageId, interfaceRecordId, mode, isPublic, pathname });
    },
    [getInterfaceStoreState, interfaceRecordId, isPublic, mode, pathname, showSnackbar, t],
  );

  const navigateToPage: ActionHandlerType = useCallback(
    ({ action, actionContext, domEvent }: OnActionArgs) => {
      const payload = (action.payload ?? {}) as NavigateToPagePayload;
      const {
        pageInputs,
        pageId: interfacePageId,
        overridePageAnimation,
        pageTransitionBehavior,
        pageParams,
      } = getNoCodeComputedData<NavigateToPagePayload>(payload, actionContext);

      if (!interfacePageId) {
        return Promise.resolve(SHOULD_NOT_PROPAGATE_UP);
      }

      const resolvedPath = getPathForInterfacePageId(interfacePageId, pageParams);

      if (resolvedPath) {
        // do not propagate the event if router push is called
        if (domEvent?.stopPropagation) {
          domEvent.stopPropagation();
        }
        onNavigate({
          path: resolvedPath,
          pageInputs,
          history: payload.history || 'push',
          target: action.payload?.target,
          pageTransitionBehavior: overridePageAnimation ? pageTransitionBehavior : undefined,
        });
      }
      return Promise.resolve(SHOULD_NOT_PROPAGATE_UP);
    },
    [getNoCodeComputedData, getPathForInterfacePageId, onNavigate],
  );

  const back: ActionHandlerType = useCallback(
    ({ action, actionContext, domEvent }: OnActionArgs) => {
      const payload = (action.payload ?? {}) as NavigateToPagePayload;
      const {
        pageInputs,
        pageId: interfacePageId,
        pageParams,
        navigateToSpecificPage,
      } = getNoCodeComputedData<NavigateToPagePayload>(payload, actionContext);

      const shouldNavigateToSpecificPage = navigateToSpecificPage && interfacePageId;

      // perform simple browser back if not navigating to specific page
      if (!shouldNavigateToSpecificPage) {
        router.back();
        return Promise.resolve(SHOULD_NOT_PROPAGATE_UP);
      }

      const path = getPathForInterfacePageId(interfacePageId, pageParams);

      if (!path) {
        return Promise.resolve(SHOULD_NOT_PROPAGATE_UP);
      }

      // do not propagate the event if router push is called
      domEvent?.stopPropagation?.();

      onNavigate({
        path,
        pageInputs,
        history: 'back',
        target: action.payload?.target,
      });

      return Promise.resolve(SHOULD_NOT_PROPAGATE_UP);
    },
    [getPathForInterfacePageId, router, onNavigate, getNoCodeComputedData],
  );

  return { navigateToPage, back, onNavigate, getPathForInterfacePageId };
}

export function useSetURLParameters() {
  const activeInterfacePageId = useInterfaceStore().hooks.useActiveInterfacePageId();
  const { getNoCodeComputedData } = useGetNoCodeComputedData();
  const getGlobalState = useGlobalStateStore().getState;
  const mode = useGlobalAppStateStore().hooks.useMode();
  const { getPathForInterfacePageId, onNavigate } = useNavigateToPage();
  const inputSchema = useInterfaceStore().hooks.page.useInputSchema(activeInterfacePageId);
  const { searchParams } = useRouter();

  const setURLParameters: ActionHandlerType = useCallback(
    ({ action, actionContext }: OnActionArgs) => {
      const pageParams = getGlobalState().pageParams;

      const path = getPathForInterfacePageId(
        activeInterfacePageId,
        Object.entries(pageParams).map(([key, value]) => ({ key, value })),
      );

      if (path) {
        const { pageInputs, mergeWithExistingQueryParams, history } =
          getNoCodeComputedData<SetURLParametersPayload>(
            (action.payload ?? {}) as SetURLParametersPayload,
            actionContext,
          );

        let queryParams: Record<string, unknown>;

        if (mode === InterfaceModes.BUILDER) {
          // In builder mode, keep all query params except page input keys
          // For page input keys, decide based on mergeWithExistingQueryParams

          // Extract page input keys from schema using existing utility
          const pageInputKeysFromSchema = parseSearchParamsFromSchema(
            inputSchema?.schema,
            searchParams,
          );
          const pageInputKeys = new Set(Object.keys(pageInputKeysFromSchema));

          // Get existing query params from URL, excluding page input keys
          // Use parseSearchParamsFromSchema with undefined schema to parse all non-page-input params
          const allQueryParams = parseSearchParamsFromSchema(undefined, searchParams);
          const existingQueryParams: Record<string, unknown> = {};
          for (const [key, value] of Object.entries(allQueryParams)) {
            if (!pageInputKeys.has(key)) {
              // Keep non-page-input query params
              existingQueryParams[key] = value;
            }
          }

          // Handle page inputs based on mergeWithExistingQueryParams
          const processedPageInputs = mergeWithExistingQueryParams
            ? { ...getGlobalState().pageInputs, ...pageInputs }
            : pageInputs || {};

          queryParams = {
            ...existingQueryParams,
            ...processedPageInputs,
          };
        } else {
          queryParams = mergeWithExistingQueryParams
            ? { ...getGlobalState().pageInputs, ...pageInputs }
            : pageInputs || {};
        }

        onNavigate({ path, history, pageInputs: queryParams });
      }

      return Promise.resolve(SHOULD_NOT_PROPAGATE_UP);
    },
    [
      activeInterfacePageId,
      getNoCodeComputedData,
      onNavigate,
      getPathForInterfacePageId,
      getGlobalState,
      mode,
      inputSchema,
      searchParams,
    ],
  );

  return { setURLParameters };
}
